import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

const GIFT_DISMISSED_PREFIX = 'doralis:cart-gift-dismissed:';
const GIFT_PRESENT_PREFIX = 'doralis:cart-gift-present:';
const GIFT_NOT_FREE_PREFIX = 'doralis:cart-gift-not-free:';
const GIFT_TIERS = ['any', 't1', 't2'];
const COUPON_APPLIED_KEY = 'doralis:upsell-coupon-applied';
const COUPON_REJECTED_KEY = 'doralis:upsell-coupon-rejected';

/** @type {CartBenefitsComponent | null} */
let activeBenefitsOwner = null;

class CartRenderError extends Error {}
class PaidGiftError extends Error {}

/**
 * sessionStorage pode estar bloqueado (Safari "Block all cookies", iframes) —
 * nunca deixar isso derrubar o sync inteiro.
 */
const storage = {
  get(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { sessionStorage.setItem(key, value); } catch { /* sem storage, sem memória — seguimos */ }
  },
  remove(key) {
    try { sessionStorage.removeItem(key); } catch { /* idem */ }
  },
};

/**
 * Automação de benefícios do carrinho:
 *  - brinde automático ao atingir a meta (respeitando remoção manual);
 *  - embalagem de presente via checkbox;
 *  - cupom do upsell aplicado/removido conforme itens sugeridos no carrinho.
 *
 * Estado 100% server-driven via data-attributes do cart-benefits-bar.liquid,
 * re-renderizados a cada morph. Cada #sync executa no máximo uma operação;
 * o morph resultante muda data-cart-sync e o observer roda o próximo passo.
 * Toda condição é idempotente — sem loops.
 */
class CartBenefitsComponent extends HTMLElement {
  #observer = new MutationObserver(() => this.#sync());

  #busy = false;

  #pendingSync = false;

  /** @type {HTMLElement | null} */
  #lockOwner = null;

  #onCartMutationIdle = () => {
    if (!this.#pendingSync || this.#busy || !this.isConnected) return;
    this.#pendingSync = false;
    this.#sync();
  };

  /**
   * Lê uma mensagem localizada renderizada pelo Liquid e substitui tokens
   * seguros como [product] e [threshold].
   * @param {string} key
   * @param {Record<string, string>} [replacements]
   */
  #text(key, replacements = {}) {
    let message = this.dataset[key] || '';
    for (const [token, value] of Object.entries(replacements)) {
      message = message.split(`[${token}]`).join(value);
    }
    return message;
  }

  connectedCallback() {
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-cart-sync'] });
    document.addEventListener('cart:mutation-idle', this.#onCartMutationIdle);
    this.#bindWrapToggle();
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    document.removeEventListener('cart:mutation-idle', this.#onCartMutationIdle);
  }

  #bindWrapToggle() {
    // Toggles vivem em markup morfável, então delegamos no documento.
    // body não é morfado, então o guard sobrevive aos morphs.
    if (document.body.dataset.cartWrapBound) return;
    document.body.dataset.cartWrapBound = 'true';

    document.addEventListener('change', (event) => {
      const checkbox = event.target;
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.closest('[data-cart-gift-wrap]')) return;

      // Busca o componente na hora do evento — nunca guardar referência,
      // o morph pode ter substituído o elemento.
      const component = checkbox
        .closest('cart-items-component')
        ?.querySelector('cart-benefits-component');
      if (!(component instanceof CartBenefitsComponent)) return;
      component.#toggleWrap(checkbox);
    });

    document.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-gift-choose]') : null;
      if (!button) return;

      const component = button
        .closest('cart-items-component')
        ?.querySelector('cart-benefits-component');
      if (!(component instanceof CartBenefitsComponent)) return;
      component.#chooseGift(button);
    });
  }

  #announce(message) {
    if (!message) return;

    const target = document.querySelector('[data-cart-gifts-live]') || document.querySelector('[data-cart-benefits-live]');
    if (!(target instanceof HTMLElement)) return;

    target.textContent = '';
    requestAnimationFrame(() => {
      const currentTarget =
        document.querySelector('[data-cart-gifts-live]') || document.querySelector('[data-cart-benefits-live]');
      if (currentTarget instanceof HTMLElement) currentTarget.textContent = message;
    });
  }

  #clearError() {
    for (const target of document.querySelectorAll('[data-cart-gifts-error], [data-cart-benefits-error]')) {
      if (!(target instanceof HTMLElement)) continue;
      target.hidden = true;
      target.textContent = '';
    }
  }

  #showError(error, fallback) {
    if (error instanceof CartRenderError) fallback = this.#text('textRenderError');
    const detail = error instanceof Error && !(error instanceof CartRenderError) ? error.message.trim() : '';
    const message = detail && detail !== fallback ? `${fallback} ${detail}` : fallback;
    const target = document.querySelector('[data-cart-gifts-error]') || document.querySelector('[data-cart-benefits-error]');

    if (target instanceof HTMLElement) {
      target.textContent = message;
      target.hidden = false;
      return;
    }

    this.#announce(message);
  }

  #setGiftBusy(container, busy) {
    if (!(container instanceof HTMLElement)) return;
    container.toggleAttribute('data-busy', busy);
    container.setAttribute('aria-busy', String(busy));
    for (const button of container.querySelectorAll('button')) {
      if (button instanceof HTMLButtonElement) button.disabled = busy;
    }
  }

  #setCartBusy(busy) {
    const cartItems = busy ? this.closest('cart-items-component') : this.#lockOwner || this.closest('cart-items-component');
    if (!(cartItems instanceof HTMLElement)) return;
    if (busy) {
      this.#lockOwner = cartItems;
      activeBenefitsOwner = this;
    } else if (activeBenefitsOwner === this) {
      // Limpa antes do evento síncrono `cart:mutation-idle`, permitindo que
      // uma segunda instância (drawer + página /cart) assuma a fila.
      activeBenefitsOwner = null;
    }

    if (typeof cartItems.setMutationBusy === 'function') {
      cartItems.setMutationBusy('benefits', busy);
      if (!busy) this.#lockOwner = null;
      return;
    }

    if (busy) {
      cartItems.setAttribute('data-benefits-busy', '');
    } else if (cartItems.hasAttribute('data-benefits-busy')) {
      cartItems.removeAttribute('data-benefits-busy');
    }

    const remainsBusy =
      cartItems.hasAttribute('data-benefits-busy') ||
      cartItems.hasAttribute('data-upsell-busy') ||
      cartItems.hasAttribute('data-cart-update-busy') ||
      cartItems.hasAttribute('data-discount-busy');
    cartItems.classList.toggle('cart-items-disabled', remainsBusy);
    if (remainsBusy) cartItems.setAttribute('aria-busy', 'true');
    else cartItems.removeAttribute('aria-busy');

    for (const button of cartItems.querySelectorAll('button[name="checkout"]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      if (remainsBusy && !button.disabled) {
        button.disabled = true;
        button.setAttribute('data-cart-busy-disabled', '');
      } else if (
        !remainsBusy &&
        button.hasAttribute('data-cart-busy-disabled') &&
        !button.hasAttribute('data-benefits-reconciliation')
      ) {
        button.disabled = false;
        button.removeAttribute('data-cart-busy-disabled');
      }
    }

    for (const accelerated of cartItems.querySelectorAll('.additional-checkout-buttons')) {
      if (!(accelerated instanceof HTMLElement)) continue;
      if (remainsBusy && !accelerated.hasAttribute('inert')) {
        accelerated.setAttribute('inert', '');
        accelerated.setAttribute('data-cart-busy-inert', '');
      } else if (!remainsBusy && accelerated.hasAttribute('data-cart-busy-inert')) {
        accelerated.removeAttribute('inert');
        accelerated.removeAttribute('data-cart-busy-inert');
      }
    }
    if (!busy) this.#lockOwner = null;
  }

  #isOtherCartMutationBusy() {
    if (activeBenefitsOwner && activeBenefitsOwner !== this) return true;

    const cartItems = this.closest('cart-items-component');
    if (!(cartItems instanceof HTMLElement)) return false;
    if (typeof cartItems.isMutationBusy === 'function') return cartItems.isMutationBusy();

    return (
      cartItems.hasAttribute('data-benefits-busy') ||
      cartItems.hasAttribute('data-upsell-busy') ||
      cartItems.hasAttribute('data-cart-update-busy') ||
      cartItems.hasAttribute('data-discount-busy')
    );
  }

  /**
   * Escolhe (ou troca) o brinde no modo "choose": remove o brinde atual,
   * se houver, e adiciona o escolhido com a property _brinde.
   * @param {Element} button
   */
  async #chooseGift(button) {
    if (window.Shopify?.designMode) return;
    if (this.#busy || this.#isOtherCartMutationBusy()) return;

    const variantId = Number(button.getAttribute('data-variant-id') || 0);
    const tier = button.getAttribute('data-tier');
    if (!variantId || !GIFT_TIERS.includes(tier)) return;

    const chooser = button.closest('.cart-gift-choice');
    const giftTitle = button.getAttribute('data-gift-title') || this.#text('textGiftFallback');
    const currentLineKey = this.getAttribute(`data-gift-${tier}-linekey`) || '';
    const currentVariantId = Number(this.getAttribute(`data-gift-${tier}-current-variant`) || 0);
    const isSwap = Boolean(currentLineKey);
    let removedCurrent = false;

    this.#clearError();
    this.#setGiftBusy(chooser, true);
    this.#setCartBusy(true);
    this.#busy = true;

    try {
      if (currentLineKey) {
        await this.#cartRequest(
          Theme.routes.cart_change_url,
          { id: currentLineKey, quantity: 0 },
          { render: false, notify: false }
        );
        removedCurrent = true;
      }
      const updatedCart = await this.#cartRequest(Theme.routes.cart_add_url, {
        items: [{ id: variantId, quantity: 1, properties: { _brinde: tier } }],
      });
      await this.#ensureGiftIsFree(updatedCart, tier, variantId);
      storage.remove(GIFT_NOT_FREE_PREFIX + tier);
      storage.set(GIFT_PRESENT_PREFIX + tier, 'true');
      storage.remove(GIFT_DISMISSED_PREFIX + tier);
      this.#announce(
        isSwap
          ? this.#text('textGiftSwapped', { product: giftTitle })
          : this.#text('textGiftAdded', { product: giftTitle })
      );
    } catch (error) {
      if (removedCurrent && currentVariantId && !(error instanceof CartRenderError)) {
        try {
          await this.#cartRequest(Theme.routes.cart_add_url, {
            items: [{ id: currentVariantId, quantity: 1, properties: { _brinde: tier } }],
          });
          storage.set(GIFT_PRESENT_PREFIX + tier, 'true');
        } catch (rollbackError) {
          this.#showError(rollbackError, this.#text('textGiftSwapRollbackError'));
          return;
        }
      }
      this.#showError(
        error,
        isSwap ? this.#text('textGiftSwapError') : this.#text('textGiftAddError')
      );
    } finally {
      this.#setGiftBusy(chooser, false);
      this.#setCartBusy(false);
      this.#busy = false;
      if (this.#pendingSync && this.isConnected) {
        this.#pendingSync = false;
        this.#sync();
      }
    }
  }

  async #toggleWrap(checkbox) {
    if (window.Shopify?.designMode) return;
    if (this.#busy || this.#isOtherCartMutationBusy()) {
      // Operação em voo: desfaz o clique; o estado real volta no próximo morph.
      checkbox.checked = !checkbox.checked;
      return;
    }

    const row = checkbox.closest('[data-cart-gift-wrap]');
    row?.setAttribute('data-busy', 'true');
    checkbox.disabled = true;
    this.#clearError();
    this.#setCartBusy(true);
    this.#busy = true;

    try {
      if (checkbox.checked) {
        await this.#cartRequest(Theme.routes.cart_add_url, {
          items: [{ id: Number(checkbox.dataset.wrapVariantId), quantity: 1, properties: { _gift_wrap: 'true' } }],
        }, { preserveDrawerScroll: true });
      } else {
        const lineKey = this.dataset.wrapLineKey;
        if (lineKey) {
          await this.#cartRequest(
            Theme.routes.cart_change_url,
            { id: lineKey, quantity: 0 },
            { preserveDrawerScroll: true }
          );
        }
      }
    } catch (error) {
      checkbox.checked = !checkbox.checked;
      this.#showError(error, this.#text('textWrapError'));
    } finally {
      // O morph geralmente já substituiu a linha; se não, reabilita.
      checkbox.disabled = false;
      row?.removeAttribute('data-busy');
      this.#setCartBusy(false);
      this.#busy = false;
      if (this.#pendingSync && this.isConnected) {
        this.#pendingSync = false;
        this.#sync();
      }
    }
  }

  /**
   * Confirma o preço líquido do brinde depois que descontos automáticos foram
   * aplicados. Se ainda houver cobrança, remove a linha imediatamente.
   * @param {Record<string, any>} cart
   * @param {string} tier
   * @param {number} variantId
   */
  async #ensureGiftIsFree(cart, tier, variantId) {
    const item = cart.items?.find(
      (entry) => Number(entry.variant_id) === variantId && entry.properties?._brinde === tier
    );
    if (!item) return;

    const allocated = (item.discount_allocations || []).reduce(
      (sum, allocation) => sum + Number(allocation.amount || 0),
      0
    );
    const lineLevelDiscount = Number(item.line_level_total_discount || 0);
    const orderShare = Math.max(0, allocated - lineLevelDiscount);
    const netPrice = Math.max(0, Number(item.final_line_price || 0) - orderShare);
    if (netPrice === 0) return;

    storage.set(GIFT_NOT_FREE_PREFIX + tier, String(variantId));
    await this.#cartRequest(Theme.routes.cart_change_url, { id: item.key, quantity: 0 });
    throw new PaidGiftError(this.#text('textGiftNotFreeError'));
  }

  async #sync() {
    if (window.Shopify?.designMode) return;
    if (this.#busy) {
      // Um morph chegou no meio de uma operação: reprocessa ao terminar,
      // senão o estado pós-operação (ex.: brinde presente) nunca é registrado.
      this.#pendingSync = true;
      return;
    }
    if (this.#isOtherCartMutationBusy()) {
      // O componente pode ter acabado de ser recriado por um morph de outra
      // operação. Aguarda o evento do lock central antes de reconciliar.
      this.#pendingSync = true;
      return;
    }

    // ---- estado por nível de brinde ----
    const tiers = GIFT_TIERS.map((key) => ({
      key,
      mode: this.getAttribute(`data-gift-${key}-mode`) || 'off',
      reached: this.getAttribute(`data-gift-${key}-reached`) === 'true',
      inCart: this.getAttribute(`data-gift-${key}-incart`) === 'true',
      variantId: Number(this.getAttribute(`data-gift-${key}-variant`) || 0),
      lineKey: this.getAttribute(`data-gift-${key}-linekey`) || '',
      currentVariantId: Number(this.getAttribute(`data-gift-${key}-current-variant`) || 0),
      quantity: Number(this.getAttribute(`data-gift-${key}-quantity`) || 0),
      count: Number(this.getAttribute(`data-gift-${key}-count`) || 0),
      netPrice: Number(this.getAttribute(`data-gift-${key}-net`) || 0),
      optionVariantIds: ['a', 'b']
        .map((option) => Number(this.getAttribute(`data-gift-${key}-option-${option}`) || 0))
        .filter(Boolean),
    }));

    for (const tier of tiers) {
      if (tier.mode === 'off' || !tier.reached) {
        storage.remove(GIFT_DISMISSED_PREFIX + tier.key);
        storage.remove(GIFT_PRESENT_PREFIX + tier.key);
        storage.remove(GIFT_NOT_FREE_PREFIX + tier.key);
      } else if (tier.inCart) {
        storage.set(GIFT_PRESENT_PREFIX + tier.key, 'true');
      } else if (storage.get(GIFT_PRESENT_PREFIX + tier.key)) {
        storage.set(GIFT_DISMISSED_PREFIX + tier.key, 'true');
        storage.remove(GIFT_PRESENT_PREFIX + tier.key);
      }
    }

    this.#setCartBusy(true);
    this.#busy = true;
    let errorFallback = this.#text('textBenefitsError');
    try {
      // Proteção de margem: um brinde persistido que deixou de receber 100%
      // de desconto nunca segue silenciosamente para o checkout.
      for (const tier of tiers) {
        if (!tier.inCart || !tier.lineKey || tier.netPrice <= 0) continue;

        storage.set(GIFT_NOT_FREE_PREFIX + tier.key, String(tier.currentVariantId));
        errorFallback = this.#text('textGiftNotFreeError');
        await this.#cartRequest(Theme.routes.cart_change_url, { id: tier.lineKey, quantity: 0 });
        throw new PaidGiftError(this.#text('textGiftNotFreeError'));
      }

      // Uma mesma property deve representar exatamente uma unidade por meta.
      // Corrige carrinhos antigos/manipulados em operações pequenas e idempotentes.
      for (const tier of tiers) {
        if (!tier.inCart || !tier.lineKey || tier.count <= 1) continue;

        errorFallback = this.#text('textGiftRemoveError', { product: this.#text('textGiftFallback') });
        await this.#cartRequest(Theme.routes.cart_change_url, { id: tier.lineKey, quantity: 0 });
        this.#announce(this.#text('textGiftDuplicateRemoved'));
        return;
      }

      for (const tier of tiers) {
        if (!tier.inCart || !tier.lineKey || tier.quantity === 1) continue;

        errorFallback = this.#text('textGiftAddError');
        await this.#cartRequest(Theme.routes.cart_change_url, { id: tier.lineKey, quantity: 1 });
        this.#announce(this.#text('textGiftQuantityCorrected'));
        return;
      }

      for (const tier of tiers) {
        if (tier.mode !== 'off' || !tier.inCart || !tier.lineKey) continue;

        const title = this.getAttribute(`data-gift-${tier.key}-title`) || this.#text('textGiftFallback');
        errorFallback = this.#text('textGiftRemoveError', { product: title });
        await this.#cartRequest(Theme.routes.cart_change_url, { id: tier.lineKey, quantity: 0 });
        this.#announce(this.#text('textGiftRemovedDisabled', { product: title }));
        return;
      }

      for (const tier of tiers) {
        if (
          tier.mode !== 'choose' ||
          !tier.inCart ||
          !tier.lineKey ||
          !tier.currentVariantId ||
          tier.optionVariantIds.includes(tier.currentVariantId)
        ) continue;

        const title = this.#text('textGiftFallback');
        errorFallback = this.#text('textGiftRemoveError', { product: title });
        await this.#cartRequest(Theme.routes.cart_change_url, { id: tier.lineKey, quantity: 0 });
        this.#announce(this.#text('textGiftRemovedDisabled', { product: title }));
        return;
      }

      // Se o produto configurado de um nível automático mudou, troca a linha
      // antiga de forma segura e restaura a opção anterior se o novo add falhar.
      for (const tier of tiers) {
        const rejectedVariantId = Number(storage.get(GIFT_NOT_FREE_PREFIX + tier.key) || 0);
        if (
          tier.mode !== 'auto' ||
          !tier.reached ||
          !tier.inCart ||
          !tier.lineKey ||
          !tier.variantId ||
          !tier.currentVariantId ||
          rejectedVariantId === tier.variantId ||
          tier.currentVariantId === tier.variantId
        ) continue;

        const title = this.getAttribute(`data-gift-${tier.key}-title`) || this.#text('textGiftFallback');
        errorFallback = this.#text('textGiftSwapError');
        let removedCurrent = false;

        try {
          await this.#cartRequest(
            Theme.routes.cart_change_url,
            { id: tier.lineKey, quantity: 0 },
            { render: false, notify: false }
          );
          removedCurrent = true;
          const updatedCart = await this.#cartRequest(Theme.routes.cart_add_url, {
            items: [{ id: tier.variantId, quantity: 1, properties: { _brinde: tier.key } }],
          });
          await this.#ensureGiftIsFree(updatedCart, tier.key, tier.variantId);
          storage.remove(GIFT_NOT_FREE_PREFIX + tier.key);
          storage.set(GIFT_PRESENT_PREFIX + tier.key, 'true');
          storage.remove(GIFT_DISMISSED_PREFIX + tier.key);
          this.#announce(this.#text('textGiftSwapped', { product: title }));
        } catch (error) {
          if (removedCurrent && !(error instanceof CartRenderError) && !(error instanceof PaidGiftError)) {
            try {
              await this.#cartRequest(Theme.routes.cart_add_url, {
                items: [{ id: tier.currentVariantId, quantity: 1, properties: { _brinde: tier.key } }],
              });
              storage.set(GIFT_PRESENT_PREFIX + tier.key, 'true');
            } catch (rollbackError) {
              this.#showError(rollbackError, this.#text('textGiftSwapRollbackError'));
              return;
            }
          }
          throw error;
        }
        return;
      }

      for (const tier of tiers) {
        if (tier.reached || !tier.inCart || !tier.lineKey) continue;

        const title = this.getAttribute(`data-gift-${tier.key}-title`) || this.#text('textGiftFallback');
        const threshold = this.getAttribute(`data-gift-${tier.key}-threshold-label`) || '';
        errorFallback = this.#text('textGiftTotalRemoveError', { product: title });
        await this.#cartRequest(Theme.routes.cart_change_url, { id: tier.lineKey, quantity: 0 });
        if (threshold) {
          this.#announce(this.#text('textGiftRemovedBelow', { product: title, threshold }));
        } else {
          this.#announce(this.#text('textGiftRemovedEmpty', { product: title }));
        }
        return;
      }

      for (const tier of tiers) {
        const dismissed = storage.get(GIFT_DISMISSED_PREFIX + tier.key) === 'true';
        const rejectedVariantId = Number(storage.get(GIFT_NOT_FREE_PREFIX + tier.key) || 0);

        if (
          tier.mode !== 'auto' ||
          !tier.variantId ||
          !tier.reached ||
          tier.inCart ||
          dismissed ||
          rejectedVariantId === tier.variantId
        ) continue;

        const title = this.getAttribute(`data-gift-${tier.key}-title`) || this.#text('textGiftFallback');
        errorFallback = this.#text('textGiftAddError');
        const updatedCart = await this.#cartRequest(Theme.routes.cart_add_url, {
          items: [{ id: tier.variantId, quantity: 1, properties: { _brinde: tier.key } }],
        });
        await this.#ensureGiftIsFree(updatedCart, tier.key, tier.variantId);
        storage.remove(GIFT_NOT_FREE_PREFIX + tier.key);
        storage.set(GIFT_PRESENT_PREFIX + tier.key, 'true');
        this.#announce(this.#text('textGiftAdded', { product: title }));
        return;
      }

      const wrap = {
        mode: this.dataset.wrapMode || 'off',
        inCart: this.dataset.wrapInCart === 'true',
        lineKey: this.dataset.wrapLineKey || '',
        variantId: Number(this.dataset.wrapVariant || 0),
        currentVariantId: Number(this.dataset.wrapCurrentVariant || 0),
        quantity: Number(this.dataset.wrapQuantity || 0),
      };

      if (
        wrap.inCart &&
        wrap.lineKey &&
        (wrap.mode !== 'on' || !wrap.variantId || wrap.currentVariantId !== wrap.variantId)
      ) {
        errorFallback = this.#text('textWrapError');
        await this.#cartRequest(Theme.routes.cart_change_url, { id: wrap.lineKey, quantity: 0 });
        this.#announce(this.#text('textWrapRemoved'));
        return;
      }

      if (wrap.inCart && wrap.lineKey && wrap.quantity !== 1) {
        errorFallback = this.#text('textWrapError');
        await this.#cartRequest(Theme.routes.cart_change_url, { id: wrap.lineKey, quantity: 1 });
        this.#announce(this.#text('textWrapQuantityCorrected'));
        return;
      }

      await this.#syncUpsellCoupon();
    } catch (error) {
      this.#showError(error, errorFallback);
    } finally {
      this.#setCartBusy(false);
      this.#busy = false;
      if (this.#pendingSync && this.isConnected) {
        this.#pendingSync = false;
        this.#sync();
      }
    }
  }

  /**
   * Aplica o cupom do upsell quando há item sugerido no carrinho e o remove
   * quando o último item sugerido sai — sem nunca descartar códigos da cliente.
   *
   * A lista completa de códigos (inclusive os que não geram desconto visível,
   * como frete grátis) vem de /cart.js (cart.discount_codes), não do Liquid.
   */
  async #syncUpsellCoupon() {
    const code = (this.dataset.upsellDiscountCode || '').trim();
    if (!code) return;

    const hasUpsellItem = this.dataset.hasUpsellItem === 'true';
    const wasApplied = storage.get(COUPON_APPLIED_KEY) === code;
    const rejectionSignature = `${code}:${this.dataset.cartSync || ''}`;
    const wasRejected = storage.get(COUPON_REJECTED_KEY) === rejectionSignature;

    // Nada a fazer nos casos comuns — evita o GET /cart.js em todo sync.
    if (hasUpsellItem && (wasApplied || wasRejected)) return;
    if (!hasUpsellItem && !wasApplied) {
      storage.remove(COUPON_REJECTED_KEY);
      return;
    }

    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    const codes = (cart.discount_codes || []).map((d) => d.code);
    const hasCode = codes.some((c) => c.toLowerCase() === code.toLowerCase());

    if (hasUpsellItem && !hasCode) {
      const data = await this.#cartRequest(Theme.routes.cart_update_url, {
        discount: [...codes, code].join(','),
      });
      const entry = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (entry && entry.applicable === false) {
        // Rejeição vale só para este estado do carrinho; ao mudar total/itens,
        // uma regra de mínimo pode passar a ser aplicável.
        storage.set(COUPON_REJECTED_KEY, rejectionSignature);
      } else {
        storage.remove(COUPON_REJECTED_KEY);
        storage.set(COUPON_APPLIED_KEY, code);
      }
      return;
    }

    if (hasUpsellItem && hasCode) {
      // O código já estava no carrinho e pertence à cliente; não assumir
      // ownership significa que o engine jamais o removerá depois.
      storage.remove(COUPON_REJECTED_KEY);
      return;
    }

    if (!hasUpsellItem && wasApplied) {
      if (hasCode) {
        const remaining = codes.filter((c) => c.toLowerCase() !== code.toLowerCase());
        await this.#cartRequest(Theme.routes.cart_update_url, { discount: remaining.join(',') });
      }
      storage.remove(COUPON_APPLIED_KEY);
    }
  }

  /**
   * Executa uma operação de carrinho com Section Rendering e morfa a seção.
   * @param {string} url
   * @param {Record<string, unknown>} body
   * @returns {Promise<Record<string, any>>} resposta do carrinho
   */
  async #cartRequest(url, body, options = {}) {
    const { render = true, notify = true, preserveDrawerScroll = false } = options;
    const sectionId = this.dataset.sectionId;
    const drawerContent = preserveDrawerScroll
      ? this.closest('cart-drawer-component')?.querySelector('.cart-drawer__content')
      : null;
    const drawerScrollTop = drawerContent instanceof HTMLElement ? drawerContent.scrollTop : null;
    const payload = render ? { ...body, sections: [sectionId] } : body;
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
    let data;

    try {
      data = await response.json();
    } catch {
      throw new Error(this.#text('textCartResponseError'));
    }

    if (!response.ok || data.status || data.errors) {
      const errors =
        typeof data.errors === 'string'
          ? data.errors
          : data.errors && typeof data.errors === 'object'
            ? Object.values(data.errors).flat().join(' ')
            : '';
      throw new Error(errors || data.description || data.message || this.#text('textTryAgain'));
    }

    // cart/add devolve somente as linhas adicionadas; nunca publique essa
    // resposta parcial como se fosse o carrinho completo. Buscamos /cart.js
    // uma única vez, em paralelo ao morph, para manter contagens e limites.
    let cartResource = Number.isFinite(data.item_count) ? data : null;
    const cartResourcePromise =
      notify && !cartResource
        ? fetch(`${Theme.routes.cart_url}.js`)
            .then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null))
            .catch(() => null)
        : Promise.resolve(cartResource);

    let renderError = null;
    if (render) {
      const html = data.sections?.[sectionId];
      if (html) {
        try {
          await morphSection(sectionId, html, { mode: 'hydration' });
          if (drawerScrollTop !== null) {
            const restoreDrawerPosition = () => {
              const currentSection = document.getElementById(`shopify-section-${sectionId}`);
              const currentContent = currentSection?.querySelector('.cart-drawer__content');
              if (currentContent instanceof HTMLElement) currentContent.scrollTop = drawerScrollTop;
            };

            // O morph e a restauração de foco podem acionar o scroll anchoring
            // em momentos diferentes. Reaplicamos agora e no próximo frame.
            restoreDrawerPosition();
            requestAnimationFrame(restoreDrawerPosition);
          }
          const cartItems = this.#lockOwner || this.closest('cart-items-component');
          if (typeof cartItems?.refreshMutationBusyState === 'function') {
            cartItems.refreshMutationBusyState();
          }
        } catch (error) {
          renderError = error;
        }
      }
    }

    cartResource = await cartResourcePromise;

    if (notify) {
      const renderedCountText = this.closest('cart-items-component')
        ?.querySelector('[ref="cartItemCount"]')
        ?.textContent?.trim();
      const renderedCount = renderedCountText ? Number(renderedCountText) : null;
      document.dispatchEvent(
        new CartUpdateEvent(cartResource || undefined, this.id, {
          source: 'cart-benefits-component',
          itemCount: renderedCount ?? cartResource?.item_count ?? 0,
          sections: data.sections,
        })
      );
    }

    if (renderError) {
      throw new CartRenderError(this.#text('textRenderError'));
    }

    return cartResource || data;
  }
}

if (!customElements.get('cart-benefits-component')) {
  customElements.define('cart-benefits-component', CartBenefitsComponent);
}
